import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { api, type Project, type User } from "./api/client";
import Icon from "./components/Icon";
import ChatPage from "./routes/ChatPage";
import CostPage from "./routes/CostPage";
import DocumentViewer from "./routes/DocumentViewer";
import HistoryPage from "./routes/HistoryPage";
import InboxPage from "./routes/InboxPage";
import LibraryPage from "./routes/LibraryPage";
import LoginPage from "./routes/LoginPage";
import ResearchAgentPage from "./routes/ResearchAgentPage";
import SettingsPage from "./routes/SettingsPage";
import StudiesPage from "./routes/StudiesPage";
import StudyDocumentPage from "./routes/StudyDocumentPage";
import TaskPage from "./routes/TaskPage";
import { setCurrentProject } from "./hooks/useCurrentProject";

type AuthState = { status: "loading" } | { status: "in"; user: User } | { status: "out" };

export default function App() {
  const [auth, setAuth] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    api
      .me()
      .then((user) => setAuth({ status: "in", user }))
      .catch(() => setAuth({ status: "out" }));
  }, []);

  if (auth.status === "loading") {
    return (
      <div className="login-screen">
        <div className="login-card">
          <span className="spinner" /> Loading...
        </div>
      </div>
    );
  }

  if (auth.status === "out") {
    return (
      <Routes>
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  return (
    <ProductionShell
      user={auth.user}
      onLogout={async () => {
        await api.logout();
        setAuth({ status: "out" });
      }}
    />
  );
}

function ProductionShell({ user, onLogout }: { user: User; onLogout: () => Promise<void> }) {
  const location = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);

  useEffect(() => {
    let cancelled = false;
    function loadProjects() {
      api
        .listProjects(true)
        .then((next) => {
          if (!cancelled) setProjects(next);
        })
        .catch(() => {
          if (!cancelled) setProjects([]);
        });
    }
    loadProjects();
    window.addEventListener("abby:projects-changed", loadProjects);
    return () => {
      cancelled = true;
      window.removeEventListener("abby:projects-changed", loadProjects);
    };
  }, [location.pathname]);

  const activeStudyId = useMemo(() => {
    const match = location.pathname.match(/^\/studies\/(\d+)/);
    return match ? Number(match[1]) : null;
  }, [location.pathname]);

  return (
    <div className="ar-host" data-tw-density="compact" data-tw-grain="true" data-tw-sidebar="wide">
      <div className="ar-page">
        <div className="ar-shell">
          <aside className="ar-sidebar studies">
            <div className="ar-brand">
              <Link className="mark" to="/studies">AbbyResearcher</Link>
              <span className="sub">hi Abby</span>
            </div>
            <nav className="ar-nav" aria-label="Primary">
              <PrimaryNavLink to="/studies" icon="home" label="Home" />
              <PrimaryNavLink to="/library" icon="library" label="Library" />
              <PrimaryNavLink to="/research" icon="research" label="Research agent" />
              <PrimaryNavLink to="/cost" icon="cost" label="Cost" />
              <PrimaryNavLink to="/settings" icon="settings" label="Settings" />
            </nav>

            <hr className="ar-rule" />
            <div className="ar-group-label">Studies</div>
            <div className="ar-studies-rail">
              {projects.filter((project) => !project.archived).map((project) => (
                <Link
                  key={project.id}
                  className={`row ${activeStudyId === project.id ? "active" : ""}`}
                  to={`/studies/${project.id}`}
                  onClick={() => setCurrentProject({ kind: "project", id: project.id })}
                >
                  <span className="swatch" style={{ background: project.color || "var(--accent)" }} />
                  <span className="lab">{project.name}</span>
                  <span className="meta">{project.counts.total}</span>
                </Link>
              ))}
              {projects.filter((project) => !project.archived).length === 0 && (
                <span className="ar-sidebar-empty">No studies yet</span>
              )}
              <Link className="row new" to="/studies">
                <span className="swatch add"><Icon name="plus" size={12} /></span>
                <span className="lab">New study</span>
              </Link>
            </div>

            <div className="ar-sidebar-foot">
              <a className="ar-btn sm" href="/api/export/all">Export all data</a>
              <span className="email">{user.email}</span>
              <button className="ar-btn ghost sm" type="button" onClick={onLogout}>
                Sign out
              </button>
            </div>
          </aside>
          <main className="ar-main production-main">
            <PageContent />
          </main>
        </div>
      </div>
    </div>
  );
}

function PrimaryNavLink({
  to,
  icon,
  label,
}: {
  to: string;
  icon: "home" | "library" | "research" | "cost" | "settings";
  label: string;
}) {
  return (
    <NavLink to={to} end={to === "/studies"}>
      <span className="ico"><Icon name={icon} size={14} /></span>
      <span>{label}</span>
    </NavLink>
  );
}

function PageContent() {
  const location = useLocation();

  return (
    <Routes key={location.pathname}>
      <Route path="/" element={<Navigate to="/studies" replace />} />
      <Route path="/studies" element={<StudiesPage />} />
      <Route path="/studies/:studyId" element={<StudiesPage />} />
      <Route path="/studies/:studyId/deliverables/:deliverableKey" element={<StudyDocumentPage />} />
      <Route path="/chat" element={<ChatPage />} />
      <Route path="/chat/:threadId" element={<ChatPage />} />
      <Route path="/tasks" element={<TaskPage />} />
      <Route path="/tasks/:templateKey" element={<TaskPage />} />
      <Route path="/library" element={<LibraryPage />} />
      <Route path="/library/:sourceId" element={<DocumentViewer />} />
      <Route path="/research" element={<ResearchAgentPage />} />
      <Route path="/inbox" element={<InboxPage />} />
      <Route path="/history" element={<HistoryPage />} />
      <Route path="/cost" element={<CostPage />} />
      <Route path="/settings" element={<SettingsPage />} />
      <Route path="*" element={<Navigate to="/studies" replace />} />
    </Routes>
  );
}
